/**
 * CDN(R2) 경로 구성 단일 source.
 *
 * - origin: 공개 R2 도메인 (예: https://sheets.ragnarokbreaker.fun). env 로 주입.
 *   Node = R2_PUBLIC_BASE, Vite = VITE_R2_PUBLIC_BASE (각 런타임에서 읽어 pure
 *   helper 에 넘긴다). 미설정 시 hard-fail.
 * - prefix: 마스터데이터 = rb-sheets, 번역 = rb-l10n. 코드에서 조립.
 * - 채널: 마스터데이터 = CDN_ENV/VITE_SHEET_ENV. 번역은 dev/staging/prod 만
 *   존재하므로 sheet 채널을 mapL10nChannel 로 접는다.
 */

export const SHEETS_PREFIX = 'rb-sheets';
export const L10N_PREFIX = 'rb-l10n';

const L10N_CHANNELS = ['dev', 'staging', 'prod'] as const;

function trimSlash(s: string): string {
  return s.replace(/\/+$/, '');
}

/** <origin>/rb-sheets. 채널은 호출부가 `/<env>/...` 로 덧붙인다. */
export function sheetsBaseUrl(origin: string): string {
  return `${trimSlash(origin)}/${SHEETS_PREFIX}`;
}

/** 개발자별 sheet 채널을 dev 로 접는다. l10n 은 dev/staging/prod 만 존재. */
export function mapL10nChannel(sheetEnv: string): string {
  return (L10N_CHANNELS as readonly string[]).includes(sheetEnv) ? sheetEnv : 'dev';
}

/**
 * Supabase env 스키마명 → rb-sheets R2 채널 슬러그.
 *
 * apply EF 가 prod 마스터데이터를 'prod' 채널에 발행하므로 'production' → 'prod'
 * 로 접는다(dev/staging/개발자명은 동일). 클라가 읽는 채널('prod')과 l10n·notices·
 * event-config 채널('prod')에 모두 정렬된다. DB 스키마명은 'production' 그대로 쓰되
 * R2 경로를 만들 때만 이 매핑을 적용한다. identity 가 기본이라 슬러그/스키마 어느
 * 개념이 들어와도 안전하다.
 */
export function masterDataChannel(envSchema: string): string {
  return envSchema === 'production' ? 'prod' : envSchema;
}

/** <origin>/rb-l10n/<mappedChannel>. 호출부가 `/l10n/<file>` 을 덧붙인다. */
export function l10nBaseUrl(origin: string, sheetEnv: string): string {
  return `${trimSlash(origin)}/${L10N_PREFIX}/${mapL10nChannel(sheetEnv)}`;
}

// V8 isolated-vm 환경엔 process 글로벌이 없으므로 호출 시점에 안전 가드.
function safeProcessEnv(): NodeJS.ProcessEnv {
  return (typeof process !== 'undefined' && process.env ? process.env : {}) as NodeJS.ProcessEnv;
}

/** R2 origin. 미설정 시 hard-fail. (Node 전용; Vite 는 import.meta.env 로 직접 읽음) */
export function getR2PublicBase(env: NodeJS.ProcessEnv = safeProcessEnv()): string {
  const v = env.R2_PUBLIC_BASE;
  if (v == null || v === '') {
    throw new Error(
      'R2_PUBLIC_BASE is not set (origin URL required, e.g. https://sheets.ragnarokbreaker.fun)'
    );
  }
  return trimSlash(v);
}

/** <origin>/rb-sheets (Node edge). */
export function getSheetsBaseUrl(env: NodeJS.ProcessEnv = safeProcessEnv()): string {
  return sheetsBaseUrl(getR2PublicBase(env));
}

/** 마스터데이터 채널 슬러그. 기본 'dev'. */
export function getCdnEnv(env: NodeJS.ProcessEnv = safeProcessEnv()): string {
  const v = env.CDN_ENV;
  if (v == null || v === '') return 'dev';
  return v;
}
